import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import institutionService, { type InstitutionDTO } from '../services/institutionService';
import { useAuth } from '../context/AuthContext';
import { UserRole, Permission } from '../types/user.types';
import { StatusOSC } from '../types/statusOSC.types';
import { STATUS_FILTER_OPTIONS, countByStatus, resolveStatusOSC } from '../utils/statusOSC.utils';
import { StatusOSCBadge, StatusOSCTimeline } from '../components/StatusOSCComponents';
import DashboardPagination from '../components/DashboardPagination';
import documentoInstitucionalService from '../services/documentoInstitucionalService';
import type { AlertaInstituicao } from '../services/documentoInstitucionalService';
import type { DocumentoInstitucional } from '../types/documentoInstitucional.types';
import { TIPOS_DOCUMENTO_LABELS } from '../types/documentoInstitucional.types';
import diligenciaDocumentoService from '../services/diligenciaDocumentoService';
import { UI_THEME } from '../ui/theme';
import { FIELD_CLASS } from '../components/ui/fieldStyles';
import { buttonClassName } from '../components/ui/buttonStyles';
import Button from '../components/ui/Button';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';
import { notifyError } from '../utils/notify';
import { toUpperText } from '../utils/formatters';

const WORKFLOW_ROLES = [UserRole.SECRETARIA, UserRole.JURIDICO];

type InstitutionStatusHistoryEntry = {
  statusNovo?: string;
  usuarioResponsavel?: string;
  usuarioResponsavelNome?: string;
};

const DashboardInstitutionsPage: React.FC = () => {
  const PAGE_SIZE_OPTIONS = [10, 20, 50];
  const navigate = useNavigate();
  const { hasRole, hasPermission, isOperadorSecretaria } = useAuth();

  const isWorkflow = WORKFLOW_ROLES.some(r => hasRole(r));
  const canCreate = hasPermission(Permission.CREATE_INSTITUTION);
  const canIniciarAnalise = hasPermission(Permission.INICIAR_ANALISE_INSTITUICAO);
  const canEnviarDiligencia = hasRole(UserRole.CONVENIOS) || hasRole(UserRole.ADMIN);

  const [institutions, setInstitutions] = useState<InstitutionDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [iniciandoAnalise, setIniciandoAnalise] = useState<Record<string, boolean>>({});
  const [statusFilter, setStatusFilter] = useState<StatusOSC | 'TODOS'>('TODOS');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [timelineInstitution, setTimelineInstitution] = useState<InstitutionDTO | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState('');
  const [timelineHistorico, setTimelineHistorico] = useState<any[]>([]);
  const [analiseIniciadaPor, setAnaliseIniciadaPor] = useState<Record<string, string>>({});

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const inputClass = FIELD_CLASS;
  const primaryButtonClass = buttonClassName({ variant: 'primary' });
  const secondaryButtonClass = buttonClassName({ variant: 'secondary' });

  // Alertas de vencimento
  const [alertas, setAlertas] = useState<AlertaInstituicao[]>([]);
  const [alertasExpanded, setAlertasExpanded] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'instituicoes' | 'documentos'>('instituicoes');

  // Diligência individual
  const [diligenciaInst, setDiligenciaInst] = useState<InstitutionDTO | null>(null);
  const [diligenciaDocs, setDiligenciaDocs] = useState<DocumentoInstitucional[]>([]);
  const [diligenciaSelecionados, setDiligenciaSelecionados] = useState<string[]>([]);
  const [diligenciaMensagem, setDiligenciaMensagem] = useState('');
  const [diligenciaLoading, setDiligenciaLoading] = useState(false);
  const [diligenciaEnviado, setDiligenciaEnviado] = useState(false);

  // Diligência em massa
  const [diligenciaTodosAberto, setDiligenciaTodosAberto] = useState(false);
  const [diligenciaTodosMensagem, setDiligenciaTodosMensagem] = useState(
    'Prezados, identificamos documentos vencidos em seu cadastro. Solicitamos a atualização dos documentos indicados para regularização junto à Prefeitura Municipal.'
  );
  const [enviandoTodos, setEnviandoTodos] = useState(false);
  const [diligenciaTodosResult, setDiligenciaTodosResult] = useState<{ ok: number; fail: number } | null>(null);

  const fetchInstitutions = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await institutionService.listForUser();
      setInstitutions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Não foi possível carregar as instituições.');
      setInstitutions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstitutions();
    if (canEnviarDiligencia) {
      documentoInstitucionalService.alertasVencimento()
        .then(data => setAlertas(data))
        .catch(() => setAlertas([]));
    }
  }, []);

  const handleIniciarAnalise = async (institution: InstitutionDTO) => {
    setIniciandoAnalise(prev => ({ ...prev, [institution.institutionId]: true }));
    try {
      await institutionService.puxarParaAnalise(institution.institutionId);
      await fetchInstitutions();
    } catch (err: any) {
      notifyError(err?.response?.data?.error || 'Não foi possível iniciar a análise.');
    } finally {
      setIniciandoAnalise(prev => ({ ...prev, [institution.institutionId]: false }));
    }
  };

  useEffect(() => {
    const loadAnaliseIniciadaPor = async () => {
      const emAnaliseIds = institutions
        .filter((inst) => inst.statusOSC === StatusOSC.EM_ANALISE && inst.institutionId)
        .map((inst) => inst.institutionId);

      if (emAnaliseIds.length === 0) {
        setAnaliseIniciadaPor({});
        return;
      }

      const historyEntries = await Promise.all(
        emAnaliseIds.map(async (institutionId) => {
          try {
            const historico = await institutionService.getHistoricoStatus(institutionId) as InstitutionStatusHistoryEntry[];
            const inicioAnalise = (historico || []).find((item) => item.statusNovo === 'EM_ANALISE');
            const responsavel =
              inicioAnalise?.usuarioResponsavelNome ||
              inicioAnalise?.usuarioResponsavel ||
              '';

            return [institutionId, responsavel] as const;
          } catch {
            return [institutionId, ''] as const;
          }
        })
      );

      setAnaliseIniciadaPor(
        historyEntries.reduce<Record<string, string>>((acc, [institutionId, responsavel]) => {
          if (responsavel) acc[institutionId] = responsavel;
          return acc;
        }, {})
      );
    };

    loadAnaliseIniciadaPor();
  }, [institutions]);

  const filteredInstitutions = useMemo(() => {
    return institutions.filter((inst) => {
      const q = search.trim().toLowerCase();
      const matchesSearch = !q ||
        (inst.razaoSocial ?? '').toLowerCase().includes(q) ||
        (inst.nomeFantasia ?? '').toLowerCase().includes(q) ||
        (inst.institutionId ?? '').toLowerCase().includes(q) ||
        (inst.cnpj ?? '').includes(q);

      const matchesStatus = statusFilter === 'TODOS' || resolveStatusOSC(inst.statusOSC) === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [institutions, search, statusFilter]);

  const counts = countByStatus(institutions);
  const paginatedInstitutions = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredInstitutions.slice(start, start + pageSize);
  }, [filteredInstitutions, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, filteredInstitutions.length, pageSize]);

  const summaryCards = [
    { label: 'Total exibido', value: filteredInstitutions.length, dot: 'bg-slate-400' },
    { label: 'Em cadastro', value: counts.emCadastro, dot: 'bg-slate-500' },
    { label: 'Em análise', value: counts.emAnalise, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Aprovadas', value: counts.aprovado, dot: 'bg-emerald-500' },
  ];

  const totalAlertasVencidos = alertas.reduce((a, i) => a + i.totalVencidos, 0);
  const totalAlertasAVencer = alertas.reduce((a, i) => a + i.totalAVencer, 0);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('TODOS');
  };

  const openTimeline = async (institution: InstitutionDTO) => {
    try {
      setTimelineInstitution(institution);
      setTimelineLoading(true);
      setTimelineError('');
      const historico = await institutionService.getHistoricoStatus(institution.institutionId);
      setTimelineHistorico(Array.isArray(historico) ? historico : []);
    } catch (err: any) {
      setTimelineHistorico([]);
      setTimelineError(err?.response?.data?.error || 'Não foi possível carregar a linha do tempo da instituição.');
    } finally {
      setTimelineLoading(false);
    }
  };

  const closeTimeline = () => {
    setTimelineInstitution(null);
    setTimelineHistorico([]);
    setTimelineError('');
    setTimelineLoading(false);
  };

  const abrirDiligencia = async (inst: InstitutionDTO) => {
    setDiligenciaInst(inst);
    setDiligenciaMensagem('');
    setDiligenciaSelecionados([]);
    setDiligenciaEnviado(false);
    try {
      const docs = await documentoInstitucionalService.listar(inst.institutionId);
      const vencidos = docs.filter(d => d.statusDocumento === 'VENCIDO' || d.statusDocumento === 'REPROVADO');
      setDiligenciaDocs(vencidos);
      if (vencidos.length > 0) setDiligenciaSelecionados(vencidos.map(d => d.id));
    } catch {
      setDiligenciaDocs([]);
    }
  };

  const fecharDiligencia = () => {
    setDiligenciaInst(null);
    setDiligenciaDocs([]);
    setDiligenciaSelecionados([]);
    setDiligenciaMensagem('');
    setDiligenciaEnviado(false);
  };

  const enviarDiligencia = async () => {
    if (!diligenciaInst || !diligenciaMensagem.trim()) return;
    setDiligenciaLoading(true);
    try {
      await diligenciaDocumentoService.enviar(diligenciaInst.institutionId, diligenciaMensagem, diligenciaSelecionados);
      setDiligenciaEnviado(true);
    } catch {
      notifyError('Erro ao enviar diligência. Tente novamente.');
    } finally {
      setDiligenciaLoading(false);
    }
  };

  const enviarDiligenciaTodos = async () => {
    if (!diligenciaTodosMensagem.trim()) return;
    const comVencidos = alertas.filter(a => a.totalVencidos > 0);
    if (comVencidos.length === 0) return;
    setEnviandoTodos(true);
    setDiligenciaTodosResult(null);
    let ok = 0;
    let fail = 0;
    for (const inst of comVencidos) {
      const docIds = inst.documentos.filter(d => d.vencido).map(d => d.id);
      try {
        await diligenciaDocumentoService.enviar(inst.instituicaoId, diligenciaTodosMensagem, docIds);
        ok++;
      } catch {
        fail++;
      }
    }
    setEnviandoTodos(false);
    setDiligenciaTodosResult({ ok, fail });
  };

  const formatCnpj = (cnpj?: string) => {
    if (!cnpj) return '-';
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  return (
    <div className="w-full space-y-6">
      <div className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
              {isSaas ? 'Organizações' : 'Gestão'}
            </p>
            <div>
              <h1 className={`text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>
                {isSaas ? 'Instituições parceiras' : 'Instituições'}
              </h1>
              <p className={`mt-2 max-w-3xl text-sm leading-6 ${bodyTextClass}`}>
                {isWorkflow
                  ? 'Exibindo as instituições vinculadas ao seu fluxo atual.'
                  : isSaas
                    ? 'Acompanhe organizações, análise cadastral e histórico de movimentações em uma visão operacional.'
                    : 'Acompanhe o cadastro das instituições, suas etapas e abra rapidamente a análise de cada uma.'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canCreate && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/cadastro-dados-institucionais')}
                className={`${primaryButtonClass}`}
              >
                Nova instituição
              </button>
            )}
            <button
              type="button"
              onClick={clearFilters}
              className={`${secondaryButtonClass}`}
            >
              Limpar filtros
            </button>
          </div>
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {canEnviarDiligencia && (
        <div className={`flex flex-wrap gap-2 rounded-3xl border p-2 ${surfaceClass}`}>
          <button
            type="button"
            onClick={() => setActiveTab('instituicoes')}
            className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${
              activeTab === 'instituicoes'
                ? `${primaryButtonClass} shadow-sm`
                : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
            }`}
          >
            Instituições
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('documentos')}
            className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
              activeTab === 'documentos'
                ? 'bg-red-600 text-white shadow-sm hover:bg-red-700'
                : 'text-slate-600 hover:bg-red-50 dark:text-slate-300 dark:hover:bg-red-950/20'
            }`}
          >
            Documentos vencidos
            {(totalAlertasVencidos + totalAlertasAVencer) > 0 && (
              <span className={`rounded-full px-2 py-0.5 text-xs ${
                activeTab === 'documentos' ? 'bg-white/20 text-white' : 'bg-danger-soft text-danger-text'
              }`}>
                {totalAlertasVencidos + totalAlertasAVencer}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Painel de alertas de vencimento — visível para CONVENIOS/ADMIN */}
      {canEnviarDiligencia && activeTab === 'documentos' && alertas.length > 0 && (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-5 shadow-sm dark:border-red-900/60 dark:bg-red-950/20">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <div>
                <span className="text-sm font-bold text-red-800 dark:text-red-300">Documentos vencidos ou a vencer em 30 dias</span>
                <span className="ml-2 text-xs text-red-600 dark:text-red-400">
                  {alertas.reduce((a, i) => a + i.totalVencidos, 0)} vencidos ·{' '}
                  {alertas.reduce((a, i) => a + i.totalAVencer, 0)} a vencer ·{' '}
                  {alertas.length} instituiç{alertas.length === 1 ? 'ão' : 'ões'}
                </span>
              </div>
            </div>
            {alertas.some(a => a.totalVencidos > 0) && (
              <button
                type="button"
                onClick={() => { setDiligenciaTodosAberto(v => !v); setDiligenciaTodosResult(null); }}
                className="inline-flex items-center gap-1.5 rounded-xl border border-amber-400 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Mandar diligência para todos
              </button>
            )}
          </div>

          {/* Formulário de diligência em massa */}
          {diligenciaTodosAberto && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
              {diligenciaTodosResult ? (
                <div className="flex flex-col items-center gap-2 py-2 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
                    <svg className="h-5 w-5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {diligenciaTodosResult.ok} diligência{diligenciaTodosResult.ok !== 1 ? 's' : ''} enviada{diligenciaTodosResult.ok !== 1 ? 's' : ''}
                    {diligenciaTodosResult.fail > 0 && ` · ${diligenciaTodosResult.fail} falha${diligenciaTodosResult.fail !== 1 ? 's' : ''}`}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">As instituições foram notificadas por e-mail.</p>
                  <Button variant="secondary" size="sm" className="mt-1" onClick={() => { setDiligenciaTodosAberto(false); setDiligenciaTodosResult(null); }}>
                    Fechar
                  </Button>
                </div>
              ) : (
                <>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                    Mensagem para todas as {alertas.filter(a => a.totalVencidos > 0).length} instituições com documentos vencidos
                  </p>
                  <textarea
                    rows={3}
                    value={diligenciaTodosMensagem}
                    onChange={e => setDiligenciaTodosMensagem(toUpperText(e.target.value))}
                    className={FIELD_CLASS}
                  />
                  <div className="mt-3 flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setDiligenciaTodosAberto(false)}>
                      Cancelar
                    </Button>
                    <Button variant="warning" size="sm" onClick={enviarDiligenciaTodos} disabled={!diligenciaTodosMensagem.trim() || enviandoTodos}>
                      {enviandoTodos ? 'Enviando...' : `Confirmar e enviar para ${alertas.filter(a => a.totalVencidos > 0).length} instituição${alertas.filter(a => a.totalVencidos > 0).length !== 1 ? 'ões' : ''}`}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="space-y-2">
            {alertas.map(inst => (
              <div key={inst.instituicaoId} className="rounded-2xl border border-danger-line bg-surface overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-red-50 transition-colors"
                  onClick={() => setAlertasExpanded(prev => prev === inst.instituicaoId ? null : inst.instituicaoId)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-fg">{inst.razaoSocial}</span>
                    {inst.totalVencidos > 0 && (
                      <span className="rounded-full bg-danger-soft px-2 py-0.5 text-[11px] font-bold text-danger-text">
                        {inst.totalVencidos} vencido{inst.totalVencidos > 1 ? 's' : ''}
                      </span>
                    )}
                    {inst.totalAVencer > 0 && (
                      <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-bold text-orange-700 dark:bg-orange-950/40 dark:text-orange-300">
                        {inst.totalAVencer} a vencer
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); abrirDiligencia(institutions.find(i => i.institutionId === inst.instituicaoId) ?? { institutionId: inst.instituicaoId, razaoSocial: inst.razaoSocial } as any); }}
                      className="rounded-lg border border-warning-line bg-warning-soft px-2.5 py-1 text-[11px] font-semibold text-warning-text hover:bg-amber-100"
                    >
                      Enviar diligência
                    </button>
                    <svg className={`h-4 w-4 text-slate-400 transition-transform ${alertasExpanded === inst.instituicaoId ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {alertasExpanded === inst.instituicaoId && (
                  <div className="border-t border-red-100 divide-y divide-red-50">
                    {inst.documentos.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between px-4 py-2 text-xs">
                        <span className="text-fg">
                          {TIPOS_DOCUMENTO_LABELS[doc.tipoDocumento as keyof typeof TIPOS_DOCUMENTO_LABELS] || doc.tipoDocumento}
                        </span>
                        <span className={`font-semibold ${doc.vencido ? 'text-danger-text' : 'text-orange-600 dark:text-orange-300'}`}>
                          {doc.vencido
                            ? `Vencido há ${Math.abs(doc.diasRestantes)} dia${Math.abs(doc.diasRestantes) !== 1 ? 's' : ''}`
                            : `Vence em ${doc.diasRestantes} dia${doc.diasRestantes !== 1 ? 's' : ''}`}
                          {doc.dataValidade && <span className="ml-1 text-fg-subtle">({new Date(doc.dataValidade + 'T00:00:00').toLocaleDateString('pt-BR')})</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {canEnviarDiligencia && activeTab === 'documentos' && alertas.length === 0 && (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass}`}>
          <p className={`text-lg font-semibold ${strongTextClass}`}>Nenhum documento vencido encontrado.</p>
          <p className={`mt-2 text-sm ${labelTextClass}`}>
            Não há documentos vencidos ou a vencer nos próximos 30 dias.
          </p>
        </div>
      )}

      {activeTab === 'instituicoes' && (
        <>
      <div className={`rounded-3xl border p-5 ${surfaceClass}`}>
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_260px_auto]">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar instituição..."
            aria-label="Buscar por razão social, nome fantasia, CNPJ ou id"
            title="Buscar por razão social, nome fantasia, CNPJ ou id"
            className={inputClass}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusOSC | 'TODOS')}
            className={inputClass}
          >
            {STATUS_FILTER_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <div className={`flex items-center justify-end text-sm ${labelTextClass}`}>
            {filteredInstitutions.length} instituições
          </div>
        </div>
      </div>

      {loading ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass} ${labelTextClass}`}>
          Carregando instituições...
        </div>
      ) : error ? (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center shadow-sm dark:border-red-900/60 dark:bg-red-950/30">
          <p className="font-semibold text-red-800 dark:text-red-200">Erro ao carregar instituições</p>
          <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      ) : filteredInstitutions.length === 0 ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass}`}>
          <p className={`text-lg font-semibold ${strongTextClass}`}>Nenhuma instituição encontrada.</p>
          <p className={`mt-2 text-sm ${labelTextClass}`}>
            Ajuste os filtros ou cadastre uma nova instituição para continuar.
          </p>
        </div>
      ) : (
        <div className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
          <Table density="comfortable">
            <Thead>
              <Tr className="border-b border-slate-200 dark:border-slate-700">
                <Th>Instituição</Th>
                <Th>CNPJ</Th>
                <Th>Etapa</Th>
                <Th align="right">Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedInstitutions.map((institution) => (
                <Tr key={institution.institutionId}>
                  <Td>
                    <div className="space-y-1">
                      <p className={`font-semibold ${strongTextClass}`}>{institution.razaoSocial}</p>
                      {institution.nomeFantasia && (
                        <p className={`text-xs ${labelTextClass}`}>{institution.nomeFantasia}</p>
                      )}
                    </div>
                  </Td>
                  <Td>{formatCnpj(institution.cnpj)}</Td>
                  <Td>
                    <div className="space-y-1">
                      <StatusOSCBadge status={institution.statusOSC || StatusOSC.EM_CADASTRO} size="sm" />
                      {institution.statusOSC === StatusOSC.EM_ANALISE && analiseIniciadaPor[institution.institutionId] && (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200">
                          análise iniciada por: {analiseIniciadaPor[institution.institutionId]}
                        </span>
                      )}
                      {institution.usuarioAprovador && (
                        <p className={`text-xs ${labelTextClass}`}>
                          Responsável: {institution.usuarioAprovador}
                        </p>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openTimeline(institution)}
                        className={`${buttonClassName({ variant: 'secondary', size: 'sm' })}`}
                      >
                        Ver timeline
                      </button>
                      {canIniciarAnalise && institution.statusOSC === StatusOSC.AGUARDANDO_ANALISE && (
                        <button
                          type="button"
                          onClick={() => handleIniciarAnalise(institution)}
                          disabled={!!iniciandoAnalise[institution.institutionId]}
                          className={`${buttonClassName({ variant: 'primary', size: 'sm' })}`}
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {iniciandoAnalise[institution.institutionId] ? 'Iniciando...' : 'Iniciar análise'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/cadastro-dados-institucionais?id=${encodeURIComponent(institution.institutionId)}`)}
                        className={`${buttonClassName({ variant: 'primary', size: 'sm' })}`}
                      >
                        {isOperadorSecretaria ? 'Gerenciar' : 'Analisar'}
                      </button>
                      {canEnviarDiligencia && (
                        <Button variant="warning-outline" size="sm" onClick={() => abrirDiligencia(institution)}>
                          Diligência
                        </Button>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <DashboardPagination
            page={page}
            pageSize={pageSize}
            totalItems={filteredInstitutions.length}
            onPageChange={setPage}
            onPageSizeChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            itemLabel="instituições"
          />
        </div>
      )}
        </>
      )}

      {timelineInstitution && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/60" onClick={closeTimeline} />
          <div className="relative z-10 w-full max-w-4xl rounded-3xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 dark:border-slate-700 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${accentTextClass}`}>Linha do tempo</p>
                <h2 className={`text-xl font-bold ${strongTextClass}`}>{timelineInstitution.razaoSocial}</h2>
                <p className={`text-sm ${labelTextClass}`}>{timelineInstitution.institutionId}</p>
              </div>
              <button
                type="button"
                onClick={closeTimeline}
                className={`${secondaryButtonClass}`}
              >
                Fechar
              </button>
            </div>

            <div className="mt-5 max-h-[70vh] overflow-y-auto pr-1">
              {analiseIniciadaPor[timelineInstitution.institutionId] && !timelineLoading && !timelineError && (
                <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
                  Análise iniciada por: <span className="font-semibold">{analiseIniciadaPor[timelineInstitution.institutionId]}</span>
                </div>
              )}
              {canIniciarAnalise && timelineInstitution.statusOSC === StatusOSC.AGUARDANDO_ANALISE && !timelineLoading && !timelineError && (
                <div className={`mb-4 rounded-2xl border px-4 py-3 ${surfaceClass}`}>
                  <p className={`mb-2 text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Análise iniciada por</p>
                  <button
                    type="button"
                    onClick={async () => {
                      await handleIniciarAnalise(timelineInstitution);
                      closeTimeline();
                    }}
                    disabled={!!iniciandoAnalise[timelineInstitution.institutionId]}
                    className={`w-full ${primaryButtonClass}`}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {iniciandoAnalise[timelineInstitution.institutionId] ? 'Iniciando...' : 'Iniciar análise'}
                  </button>
                </div>
              )}
              {timelineLoading ? (
                <div className={`rounded-2xl border p-8 text-center text-sm ${surfaceClass} ${labelTextClass}`}>
                  Carregando linha do tempo...
                </div>
              ) : timelineError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                  {timelineError}
                </div>
              ) : (
                <StatusOSCTimeline historico={timelineHistorico} />
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modal de Diligência */}
      {diligenciaInst && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40" onClick={fecharDiligencia} />
          <div className="relative z-10 w-full max-w-lg rounded-2xl bg-surface shadow-2xl">
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-warning-text">Diligência</p>
                <h2 className="text-base font-bold text-fg">{diligenciaInst.razaoSocial}</h2>
              </div>
              <button type="button" onClick={fecharDiligencia} className="text-fg-subtle hover:text-slate-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {diligenciaEnviado ? (
              <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-soft">
                  <svg className="h-6 w-6 text-success-text" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="text-sm font-semibold text-fg">Diligência enviada com sucesso</p>
                <p className="text-xs text-fg-muted">A instituição foi notificada por e-mail.</p>
                <button type="button" onClick={fecharDiligencia} className="mt-2 rounded-xl bg-slate-900 px-5 py-2 text-sm font-medium text-white">
                  Fechar
                </button>
              </div>
            ) : (
              <div className="px-6 py-5 space-y-4">
                {/* Documentos vencidos/reprovados */}
                {diligenciaDocs.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">Documentos com problema</p>
                    <div className="space-y-1.5 max-h-44 overflow-y-auto">
                      {diligenciaDocs.map(doc => (
                        <label key={doc.id} className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-line px-3 py-2 text-xs hover:bg-surface-muted">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-amber-600"
                            checked={diligenciaSelecionados.includes(doc.id)}
                            onChange={e => setDiligenciaSelecionados(prev =>
                              e.target.checked ? [...prev, doc.id] : prev.filter(id => id !== doc.id)
                            )}
                          />
                          <span className="flex-1 text-fg">
                            {TIPOS_DOCUMENTO_LABELS[doc.tipoDocumento as keyof typeof TIPOS_DOCUMENTO_LABELS] || doc.tipoDocumento}
                          </span>
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${doc.statusDocumento === 'VENCIDO' ? 'bg-danger-soft text-danger-text' : 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300'}`}>
                            {doc.statusDocumento === 'VENCIDO' ? 'Vencido' : 'Reprovado'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mensagem */}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-fg-muted">
                    Mensagem para a instituição
                  </label>
                  <textarea
                    rows={4}
                    value={diligenciaMensagem}
                    onChange={e => setDiligenciaMensagem(toUpperText(e.target.value))}
                    placeholder="Descreva o que a instituição precisa providenciar..."
                    className={FIELD_CLASS}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="secondary" onClick={fecharDiligencia}>
                    Cancelar
                  </Button>
                  <Button variant="warning" onClick={enviarDiligencia} disabled={!diligenciaMensagem.trim() || diligenciaLoading}>
                    {diligenciaLoading ? 'Enviando...' : 'Enviar diligência'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardInstitutionsPage;
